/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect } from "react";
import { useParams, useSearchParams, useLocation } from "react-router-dom";
import { PersonalDetails, MemorialContextType, GalleryImage } from "../types/memorial";
import { supabase } from "@/integrations/supabase/client";

const initialPersonalDetails: PersonalDetails = {
  firstName: '',
  lastName: '',
  birthDate: '',
  deathDate: '',
  birthPlace: '',
  deathPlace: '',
  introduction: '',
  profileImage: null,
  featuredPhotos: [],
  galleryImages: [],
  lifeStages: [],
  tributes: [],
  education: '',
  profession: '',
  spirituality: '',
  interests: [],
  family: [],
  socialLinks: {
    facebook: '',
    instagram: '',
    youtube: '',
    linkedin: '',
    twitter: '',
    website: '',
    spotify: '',
    blog: '',
    obituary: '',
    news_article: '',
    charity: '',
    memorial_fund: '',
    other: ''
  }
};

const defaultContext: MemorialContextType = {
  id: "",
  personalDetails: initialPersonalDetails,
  updatePersonalDetails: () => {},
  addGalleryImage: () => {},
  removeGalleryImage: () => {},
  updateGalleryImage: () => {},
  getAllImages: () => [],
  addTribute: () => {},
  removeTribute: () => {}
};

const MemorialContext = createContext<MemorialContextType>(defaultContext);

export const useMemorial = () => useContext(MemorialContext);

export const MemorialProvider = ({ children }: { children: React.ReactNode }) => {
  const { id: urlId } = useParams<{ id: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryMemorialId = searchParams.get('memorialId');
  
  const [personalDetails, setPersonalDetails] = useState<PersonalDetails>(initialPersonalDetails);
  const [id, setId] = useState<string>("");

  useEffect(() => {
    const effectiveId = urlId || queryMemorialId;

    if (effectiveId) {
      setId(effectiveId);
      
      const fetchMemorialDetails = async () => {
        try {
          const { data, error } = await supabase
            .from('memorials_public')
            .select('*')
            .eq('id', effectiveId)
            .maybeSingle();
            
          if (error) {
            console.error("[MEMORIAL_CONTEXT] Error fetching memorial:", error);
            return;
          }
          
          if (data) {
            setPersonalDetails(prevDetails => ({
              ...prevDetails,
              firstName: data.first_name || '',
              lastName: data.last_name || '',
              birthDate: data.birth_date || '',
              deathDate: data.death_date || '',
              birthPlace: data.place_of_birth || '',
              deathPlace: data.place_of_death || '',
              introduction: data.introduction || '',
              profileImage: data.profile_picture_url || null,
              isDoubleMemorial: data.is_double_memorial || false,
              person2FirstName: data.person2_first_name || '',
              person2LastName: data.person2_last_name || '',
              person2BirthDate: data.person2_birth_date || '',
              person2DeathDate: data.person2_death_date || '',
              person2BirthPlace: data.person2_place_of_birth || '',
              person2DeathPlace: data.person2_place_of_death || '',
            }));
          }
        } catch (err) {
          console.error("[MEMORIAL_CONTEXT] Error:", err);
        }
      };
      
      fetchMemorialDetails();
    }
  }, [urlId, queryMemorialId, location]);

  const updatePersonalDetails = (details: Partial<PersonalDetails>) => {
    setPersonalDetails(prevDetails => ({
      ...prevDetails,
      ...details
    }));
  };

  const addGalleryImage = (image: GalleryImage) => {
    setPersonalDetails(prev => ({
      ...prev,
      galleryImages: [...prev.galleryImages, image]
    }));
  };

  const removeGalleryImage = (imageId: string) => {
    setPersonalDetails(prev => ({
      ...prev,
      galleryImages: prev.galleryImages.filter(img => img.id !== imageId)
    }));
  };

  const updateGalleryImage = (imageId: string, updates: Partial<GalleryImage>) => {
    setPersonalDetails(prev => ({
      ...prev,
      galleryImages: prev.galleryImages.map(img => 
        img.id === imageId ? { ...img, ...updates } : img
      )
    }));
  };

  const getAllImages = (): GalleryImage[] => {
    const allImages: GalleryImage[] = [];
    
    if (personalDetails.profileImage) {
      allImages.push({
        id: 'profile',
        src: personalDetails.profileImage,
        alt: `${personalDetails.firstName} ${personalDetails.lastName} Profile Picture`
      });
    }
    
    personalDetails.featuredPhotos.forEach((photo, index) => {
      allImages.push({
        id: `featured-${index}`,
        src: photo,
        alt: `Featured Photo ${index + 1}`
      });
    });
    
    allImages.push(...personalDetails.galleryImages);
    
    return allImages.filter((image, index, self) =>
      index === self.findIndex((t) => t.src === image.src)
    );
  };

  const addTribute = (author: string, content: string) => {
    const newTribute = {
      id: Date.now().toString(),
      author,
      content,
      createdAt: new Date()
    };

    setPersonalDetails(prev => ({
      ...prev,
      tributes: [newTribute, ...prev.tributes]
    }));
  };

  const removeTribute = (tributeId: string) => {
    setPersonalDetails(prev => ({
      ...prev,
      tributes: prev.tributes.filter(tribute => tribute.id !== tributeId)
    }));
  };

  return (
    <MemorialContext.Provider value={{ 
      id,
      personalDetails, 
      updatePersonalDetails,
      addGalleryImage,
      removeGalleryImage,
      updateGalleryImage,
      getAllImages,
      addTribute,
      removeTribute
    }}>
      {children}
    </MemorialContext.Provider>
  );
};
